define('businessServices/signup/signupNumberAutoAssign',[
    'businessServices/numberProvisioning/numberProvisioner',
    'businessServices/signup/signupSessionStore',
    'common/converters/phoneNumberFormatter',
    'common/promises/promiseFactory',
    'businessServices/signup/signupSessionSingleton',
    'constants/numberPickerConstants'
], function() {
    const PhoneNumberFormatterConstructor = require('common/converters/phoneNumberFormatter');
    const _phoneNumberFormatter = new PhoneNumberFormatterConstructor();

    const PromiseFactoryConstructor = require('common/promises/promiseFactory');
    const _promiseFactory = new PromiseFactoryConstructor();

    const _numberPickerConstants = require('constants/numberPickerConstants');
    const _signupSessionState = require('businessServices/signup/signupSessionSingleton');

    const RESERVED_STATUS = "reserved";

    let _numberProvisioner = null;
    let _signupSessionStore = null;

    const _tryReserveByGeoLocation = () => {
        return _promiseFactory.deferIndefinitely(promise => {
            _numberProvisioner.searchByIpAddress()
                .fail(() => promise.resolve(false))
                .done((result) => {
                    const availableNumbers = result && result.data && result.data.availableNumbers;
                    if (!availableNumbers || availableNumbers.length === 0) {
                        promise.resolve(false);
                        return;
                    }

                    const { data } = result;
                    const phoneNumber = _phoneNumberFormatter.toEOneSixFour(availableNumbers[0]);
                    const region = data.regionCode;
                    const city = data.city;
                    const searchType = _numberPickerConstants.searchTypeEnumeration.default;
                    _signupSessionStore.reservePhoneNumber({phoneNumber, region, city, searchType})
                        .fail(() => promise.resolve(false))
                        .done((reservation) => promise.resolve(reservation.status === RESERVED_STATUS));
                });
        });
    };

    // An auto-assigned number never comes from a number type the customer picked, so any stale
    // selection is cleared. A recorded number type is what tells signup a reservation was expected
    const _recordAutoAssignment = (isNumberReserved, numberIntentSelection) => {
        return _promiseFactory.deferIndefinitely(promise => {
            _signupSessionStore.storeBypassNumberSelection(true);
            _signupSessionStore.saveNumberTypeSelection(null)
                .fail(promise.reject)
                .done(() => {
                    const recordIntentPromise = numberIntentSelection === null ?
                        _promiseFactory.defer(p => p.resolve()) :
                        _signupSessionStore.saveNumberIntentSelection(numberIntentSelection);

                    recordIntentPromise
                        .fail(promise.reject)
                        .done(() => {
                            _signupSessionStore.setPhoneNumberCompleted(true)
                                .fail(promise.reject)
                                .done(() => promise.resolve({ isNumberReserved }));
                        });
                });
        });
    };

    const _autoAssignNumberByGeolocation = ({numberIntentSelection = null} = {}) => {
        return _promiseFactory.deferIndefinitely(promise => {
            _tryReserveByGeoLocation()
                .fail(promise.reject)
                .done((isNumberReserved) => {
                    _recordAutoAssignment(isNumberReserved, numberIntentSelection)
                        .fail(promise.reject)
                        .done(promise.resolve);
                });
        });
    };

    const _hasReservedNumber = () => {
        const signupDocument = _signupSessionState.signupDocument;
        return !!(signupDocument && signupDocument.reservedPhoneNumber);
    };

    // True when the held number came from auto-assignment rather than a deliberate pick.
    // Picking a number explicitly clears the bypass flag, so it distinguishes the two.
    const _isNumberAutoAssigned = () => {
        return _signupSessionState.isBypassNumberSelection() === true && _hasReservedNumber();
    };

    const _releaseAutoAssignedNumber = () => {
        return _promiseFactory.deferIndefinitely(promise => {
            const reservedPhoneNumber = _signupSessionState.signupDocument.reservedPhoneNumber;
            _signupSessionStore.cancelPhoneNumberReservation(reservedPhoneNumber)
                .fail(promise.reject)
                .done(() => {
                    _signupSessionStore.storeBypassNumberSelection(false);
                    _signupSessionStore.saveNumberTypeSelection(null)
                        .fail(promise.reject)
                        .done(() => {
                            _signupSessionStore.setPhoneNumberCompleted(false)
                                .fail(promise.reject)
                                .done(() => promise.resolve(true));
                        });
                });
        });
    };

    const _init = () => {
        const NumberProvisionerConstructor = require('businessServices/numberProvisioning/numberProvisioner');
        _numberProvisioner = new NumberProvisionerConstructor();
        _numberProvisioner.init();

        const SignupSessionStoreConstructor = require('businessServices/signup/signupSessionStore');
        _signupSessionStore = new SignupSessionStoreConstructor();
        _signupSessionStore.init();
    };

    return function() {
        let self = this;
        self.init = _init;
        self.autoAssignNumberByGeolocation = _autoAssignNumberByGeolocation;
        self.hasReservedNumber = _hasReservedNumber;
        self.isNumberAutoAssigned = _isNumberAutoAssigned;
        self.releaseAutoAssignedNumber = _releaseAutoAssignedNumber;
    };
});

